import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/design/design/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import ComponentLayout from '~/src/layouts/component-layout';
import { AccessibilityLink } from '~/src/components/accessibility-link';
import { Box } from '@primer/react';
import { Caption } from '@primer/gatsby-theme-doctocat';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = ComponentLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">





    <p>{`ConfirmationDialog is a special kind of dialog with rigid behavior that's used to confirm user actions. It always has exactly two buttons: one to cancel the action and one to confirm it. The component provides no custom rendering capabilities and focuses on consistency across confirmations.`}</p>
    <h2>{`Anatomy`}</h2>
    <img width="960" alt="Anatomy of a ConfirmationDialog showing header, body content, and action buttons" src="https://github.com/user-attachments/assets/fb545bc1-0b21-43dd-bfb4-378ffef894a8" />
    <Caption mdxType="Caption">Anatomy of a ConfirmationDialog.</Caption>
    <h3>{`Header region`}</h3>
    <p>{`The `}<strong parentName="p">{`header`}</strong>{` region contains the confirmation title and close button. The title should clearly describe the action being confirmed, typically phrased as a question (e.g., "Delete repository?", "Discard changes?").`}</p>
    <h3>{`Body content`}</h3>
    <p>{`The `}<strong parentName="p">{`body`}</strong>{` provides additional context about the action's consequences. This content is passed as children to the ConfirmationDialog component.`}</p>
    <h3>{`Action buttons`}</h3>
    <p>{`The `}<strong parentName="p">{`action region`}</strong>{` contains exactly two buttons: a cancel button (left) and a confirm button (right). The confirm button can be styled as `}<inlineCode parentName="p">{`normal`}</inlineCode>{` (default), `}<inlineCode parentName="p">{`primary`}</inlineCode>{`, or `}<inlineCode parentName="p">{`danger`}</inlineCode>{` depending on the severity of the action.`}</p>
    <h2>{`Usage`}</h2>
    <p>{`Use ConfirmationDialog for actions that require explicit user confirmation, especially when:`}</p>
    <ul>
      <li parentName="ul">{`The action is destructive or irreversible`}</li>
      <li parentName="ul">{`The action has significant consequences`}</li>
      <li parentName="ul">{`You need a standardized confirmation pattern`}</li>
    </ul>
    <h3>{`When not to use`}</h3>
    <ul>
      <li parentName="ul">{`For complex forms or multi-step processes (use `}<a parentName="li" {...{
          "href": "/components/dialog"
        }}>{`Dialog`}</a>{` instead)`}</li>
      <li parentName="ul">{`When you need custom header or footer content`}</li>
      <li parentName="ul">{`For non-destructive actions that can be easily undone`}</li>
    </ul>
    <h2>{`API`}</h2>
    <p>{`ConfirmationDialog is built on top of the Dialog component but provides a simplified, constrained API:`}</p>
    <h3>{`Required props`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`<ConfirmationDialog
  title="Delete repository?"
  onClose={(gesture) => {
    // Handle the result
    // gesture can be: 'confirm', 'cancel', 'close-button', or 'escape'
  }}
>
  This action cannot be undone.
</ConfirmationDialog>
`}</code></pre>
    <h3>{`Optional props`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`<ConfirmationDialog
  title="Delete repository?"
  onClose={handleClose}
  cancelButtonContent="Cancel" // Default: "Cancel"
  confirmButtonContent="Delete" // Default: "OK"
  confirmButtonType="danger" // Options: 'normal', 'primary', 'danger'
  width="medium" // Options: 'small', 'medium', 'large', 'xlarge'
  height="auto" // Options: 'small', 'large', 'auto'
  className="custom-class"
>
  Content describing the consequences of the action.
</ConfirmationDialog>
`}</code></pre>
    <h2>{`Examples`}</h2>
    <h3>{`Basic confirmation`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`function DeleteExample() {
  const [isOpen, setIsOpen] = useState(false)

  const handleClose = (gesture) => {
    if (gesture === 'confirm') {
      // Perform the delete action
      console.log('Confirmed!')
    }
    setIsOpen(false)
  }

  return (
    <>
      <Button onClick={() => setIsOpen(true)}>Delete item</Button>
      {isOpen ? (
        <ConfirmationDialog
          title="Delete item?"
          onClose={handleClose}
        >
          This action cannot be undone.
        </ConfirmationDialog>
      ) : null}
    </>
  )
}
`}</code></pre>
    <h3>{`Dangerous action`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<ConfirmationDialog
  title="Delete repository?"
  onClose={handleClose}
  confirmButtonContent="Delete repository"
  confirmButtonType="danger"
>
  This will permanently delete the repository and all of its contents.
  This action cannot be undone.
</ConfirmationDialog>
`}</code></pre>
    <h3>{`Using the useConfirm hook`}</h3>
    <p>{`For programmatic confirmations, use the `}<inlineCode parentName="p">{`useConfirm`}</inlineCode>{` hook:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`function ExampleWithHook() {
  const confirm = useConfirm()

  const handleAction = async () => {
    const confirmed = await confirm({
      title: 'Are you sure?',
      content: 'This action cannot be undone.',
      confirmButtonType: 'danger'
    })

    if (confirmed) {
      // Perform the action
    }
  }

  return <Button onClick={handleAction}>Delete</Button>
}
`}</code></pre>
    <h2>{`Behavior`}</h2>
    <h3>{`Focus management`}</h3>
    <ul>
      <li parentName="ul">{`When the confirmation is dangerous (`}<inlineCode parentName="li">{`confirmButtonType="danger"`}</inlineCode>{`), focus is placed on the Cancel button`}</li>
      <li parentName="ul">{`When the confirmation is not dangerous, focus is placed on the Confirm button`}</li>
      <li parentName="ul">{`Focus returns to the triggering element when the dialog closes`}</li>
    </ul>
    <h3>{`Button types`}</h3>
    <ul>
      <li parentName="ul"><strong parentName="li">{`normal`}</strong>{` (default): Standard button styling`}</li>
      <li parentName="ul"><strong parentName="li">{`primary`}</strong>{`: Emphasized styling for important confirmations`}</li>
      <li parentName="ul"><strong parentName="li">{`danger`}</strong>{`: Red styling for destructive actions`}</li>
    </ul>
    <h3>{`Closing behavior`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`onClose`}</inlineCode>{` callback receives a gesture parameter indicating how the dialog was closed:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`'confirm'`}</inlineCode>{`: User clicked the confirm button`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`'cancel'`}</inlineCode>{`: User clicked the cancel button`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`'close-button'`}</inlineCode>{`: User clicked the X close button`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`'escape'`}</inlineCode>{`: User pressed the Escape key`}</li>
    </ul>
    <h2>{`Accessibility`}</h2>
    <AccessibilityLink label="ConfirmationDialog" mdxType="AccessibilityLink" />
    <p>{`ConfirmationDialog follows dialog accessibility patterns and includes:`}</p>
    <ul>
      <li parentName="ul">{`Uses `}<inlineCode parentName="li">{`role="alertdialog"`}</inlineCode>{` to indicate urgent content requiring user attention`}</li>
      <li parentName="ul">{`Proper focus management and focus trapping`}</li>
      <li parentName="ul">{`Keyboard support (Tab, Shift+Tab, Escape)`}</li>
      <li parentName="ul">{`Screen reader announcements for the confirmation content`}</li>
      <li parentName="ul">{`Clear button labeling for screen readers`}</li>
    </ul>
    <h3>{`Screen reader considerations`}</h3>
    <ul>
      <li parentName="ul">{`The title serves as the accessible name for the dialog`}</li>
      <li parentName="ul">{`Body content provides additional context`}</li>
      <li parentName="ul">{`Button labels should be descriptive and action-oriented`}</li>
      <li parentName="ul">{`The dangerous confirmation focus pattern helps prevent accidental confirmations`}</li>
    </ul>
    <h2>{`Related components`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/dialog"
        }}>{`Dialog`}</a>{`: For general modal content and custom layouts`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/button"
        }}>{`Button`}</a>{`: For action triggers that may open confirmation dialogs`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/banner"
        }}>{`Banner`}</a>{`: For non-modal notifications and warnings`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      